import React, { useEffect, useState } from "react";
import { RaceOrderManager, RacerEntry } from "../RaceOrderManager";
import { GameObject } from "@needle-tools/engine";
import { GameStateEvents, RaceOrderEvents } from "../Consts";
import { StateManager } from "../GameStateManager";

export type Racer = Pick<RacerEntry, "racerName" | "racerNumber">;

const RaceOrderContext = React.createContext<{
  raceOrder: Racer[];
  setRaceOrder: React.Dispatch<React.SetStateAction<Racer[]>>;
}>({ raceOrder: [], setRaceOrder: () => {} });

interface RaceOrderProviderProps {
  children: React.ReactNode;
}

export function RaceOrderProvider({ children }: RaceOrderProviderProps) {
  const [raceOrder, setRaceOrder] = useState<Racer[]>([]);

  const handleRaceOrderUpdate = () => {
    const raceOrderManager = GameObject.findObjectOfType(RaceOrderManager);
    if (!raceOrderManager) {
      setTimeout(() => {
        handleRaceOrderUpdate();
      }, 200);
    } else {
      const parsed = raceOrderManager?.ReturnRacers().map((racer) => ({
        racerName: racer.racerName,
        racerNumber: racer.racerNumber,
      }));

      if (!parsed) return;
      setRaceOrder(parsed);
    }
  };

  useEffect(() => {
    const gameStateManager = StateManager.getInstance();

    gameStateManager.subscribeToEvent(
      GameStateEvents.ON_ENTER_RACE_IN_PROGRESS_STATE,
      handleRaceOrderUpdate,
    );
    let raceOrderManager: RaceOrderManager | null = null;
    const subscribeRaceOrder = () => {
      raceOrderManager = GameObject.findObjectOfType(RaceOrderManager);
      if (!raceOrderManager) {
        setTimeout(() => {
          subscribeRaceOrder();
        }, 200);
      } else {
        raceOrderManager?.subscribeToEvent(
          RaceOrderEvents.ON_RACE_ORDER_UPDATED,
          handleRaceOrderUpdate,
        );
      }
    };

    subscribeRaceOrder();

    return () => {
      gameStateManager.unsubscribeFromEvent(
        GameStateEvents.ON_ENTER_RACE_IN_PROGRESS_STATE,
        handleRaceOrderUpdate,
      );

      raceOrderManager?.unsubscribeFromEvent(
        RaceOrderEvents.ON_RACE_ORDER_UPDATED,
        handleRaceOrderUpdate,
      );
    };
  }, []);

  return (
    <RaceOrderContext.Provider value={{ raceOrder, setRaceOrder }}>
      {children}
    </RaceOrderContext.Provider>
  );
}

export function useRaceOrder() {
  const context = React.useContext(RaceOrderContext);
  if (context === undefined) {
    throw new Error("useRaceOrder must be used within a RaceOrderProvider");
  }
  return context;
}
